type TweenValue = number | string;
type TweenState = Record<string, TweenValue>;
type TweenRenderState<TState extends TweenState> = {
   [Key in keyof TState]: TState[Key];
};

interface TweenOptions<TState extends TweenState> {
   from: TState;
   to: TState;
   duration?: number;
   delay?: number;
   easing?: string;
   start?: () => void;
   render: (state: TweenRenderState<TState>) => void;
   finish?: () => void;
}

type EasingFunction = (progress: number) => number;

const easings: Record<string, EasingFunction> = {
   bounce: progress => 1 - easeOutBounce(1 - progress),
   bouncePast: progress => easeOutBack(progress),
   easeFromTo: progress => easeInOutQuad(progress),
   easeInExpo: progress => (progress === 0 ? 0 : 2 ** (10 * progress - 10)),
   easeInOutQuad,
   easeInQuad: progress => progress * progress,
   easeOutQuad: progress => 1 - (1 - progress) * (1 - progress),
   swingFromTo: progress => 0.5 - Math.cos(progress * Math.PI) / 2,
};

export function tween<TState extends TweenState>(options: TweenOptions<TState>) {
   const duration = options.duration ?? 500;
   const delay = options.delay ?? 0;
   const easing = easings[options.easing ?? ''] ?? (progress => progress);

   return new Promise<void>(resolve => {
      let firstFrameTimestamp: number | null = null;
      let startTimestamp: number | null = null;
      let hasStarted = false;

      const animationStep = (timestamp: number) => {
         firstFrameTimestamp ??= timestamp;

         if (timestamp - firstFrameTimestamp < delay) {
            requestAnimationFrame(animationStep);
            return;
         }

         if (!hasStarted) {
            hasStarted = true;
            startTimestamp = timestamp;
            options.start?.();
         }

         const rawProgress = duration === 0 ? 1 : (timestamp - startTimestamp!) / duration;
         const progress = Math.min(Math.max(rawProgress, 0), 1);
         options.render(interpolateState(options.from, options.to, easing(progress)));

         if (progress < 1) {
            requestAnimationFrame(animationStep);
            return;
         }

         options.finish?.();
         resolve();
      };

      requestAnimationFrame(animationStep);
   });
}

function interpolateState<TState extends TweenState>(
   from: TState,
   to: TState,
   progress: number
): TweenRenderState<TState> {
   return Object.fromEntries(
      Object.entries(to).map(([key, toValue]) => {
         const fromValue = from[key];
         return [key, interpolateValue(fromValue, toValue, progress)];
      })
   ) as TweenRenderState<TState>;
}

function interpolateValue(from: TweenValue, to: TweenValue, progress: number): TweenValue {
   if (typeof from === 'number' && typeof to === 'number') {
      return from + (to - from) * progress;
   }

   if (typeof from === 'string' && typeof to === 'string') {
      const fromColor = parseHexColor(from);
      const toColor = parseHexColor(to);

      if (fromColor && toColor) {
         return formatHexColor(
            fromColor.map((channel, index) =>
               Math.round(channel + (toColor[index] - channel) * progress)
            )
         );
      }
   }

   return progress < 1 ? from : to;
}

function parseHexColor(color: string): [number, number, number] | null {
   const match = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);

   if (!match) {
      return null;
   }

   return [
      Number.parseInt(match[1], 16),
      Number.parseInt(match[2], 16),
      Number.parseInt(match[3], 16),
   ];
}

function formatHexColor(channels: number[]) {
   return `#${channels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

function easeInOutQuad(progress: number) {
   return progress < 0.5 ? 2 * progress * progress : 1 - (-2 * progress + 2) ** 2 / 2;
}

function easeOutBounce(progress: number) {
   const firstBounce = 1 / 2.75;
   const secondBounce = 2 / 2.75;
   const thirdBounce = 2.5 / 2.75;

   if (progress < firstBounce) {
      return 7.5625 * progress * progress;
   }

   if (progress < secondBounce) {
      const adjustedProgress = progress - 1.5 / 2.75;
      return 7.5625 * adjustedProgress * adjustedProgress + 0.75;
   }

   if (progress < thirdBounce) {
      const adjustedProgress = progress - 2.25 / 2.75;
      return 7.5625 * adjustedProgress * adjustedProgress + 0.9375;
   }

   const adjustedProgress = progress - 2.625 / 2.75;
   return 7.5625 * adjustedProgress * adjustedProgress + 0.984_375;
}

function easeOutBack(progress: number) {
   const overshoot = 1.701_58;
   return 1 + (overshoot + 1) * (progress - 1) ** 3 + overshoot * (progress - 1) ** 2;
}
